import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Drawer,
  Toolbar,
  Typography,
  AppBar,
  IconButton,
  CssBaseline,
  ScopedCssBaseline,
  ThemeProvider,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NotificationDropdown from "./NotificationDropdown";
import theme from "../utils/theme";
import Sidebar from "./Sidebar";
import SidebarOptions from "./SidebarOptions";

const drawerWidth = 270;
const collapsedWidth = 100;

const styles = {
  root: {
    display: "flex",
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
  },
  appBar: (collapsed) => ({
    zIndex: (theme) => theme.zIndex.drawer + 1,
    bgcolor: theme.palette.primary.contrastText,
    color: theme.palette.text.primary,
    boxShadow:
      "0 4px 16px rgba(44,62,80,0.10), 0 1.5px 4px rgba(44,62,80,0.08)",
    width: { md: `calc(100% - ${collapsed ? collapsedWidth : drawerWidth}px)` },
    ml: { md: `${collapsed ? collapsedWidth : drawerWidth}px` },
    transition: "width 0.3s, margin-left 0.3s",
  }),
  toolbar: {
    minHeight: 64,
    display: "flex",
    justifyContent: "space-between",
    px: { md: 3, xs: 1 },
  },
  drawerNav: (collapsed) => ({
    width: { md: collapsed ? collapsedWidth : drawerWidth },
    flexShrink: { md: 0 },
    transition: "width 0.3s",
  }),
  drawerPaper: (collapsed) => ({
    width: collapsed ? collapsedWidth : drawerWidth,
    bgcolor: theme.palette.secondary.main,
    color: theme.palette.text.light,
    transition: "width 0.3s",
  }),
  drawerPaperMobile: {
    width: drawerWidth,
    bgcolor: theme.palette.secondary.main,
    color: theme.palette.text.light,
  },
  main: {
    flexGrow: 1,
    bgcolor: theme.palette.background.default,
    p: { xs: 1, md: 3 },
    mt: 8,
    overflow: "auto",
    overflowX: "hidden",
    transition: "background 0.3s, box-shadow 0.3s",
  },
};

export default function AppLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [selectedPage, setSelectedPage] = useState("");
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [canGoBack, setCanGoBack] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get navItems directly from SidebarOptions component
  const navItems = SidebarOptions();

  const handleLogout = () => {
    localStorage.removeItem("QC_accessToken");
    navigate("/login");
  };

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleCollapseToggle = () => setCollapsed((prev) => !prev);

  const handleGoBack = () => {
    navigate(-1);
  };

  const toggleSubmenu = (label) => {
    if (label) {
      setOpenSubmenus((prev) => ({
        ...prev,
        [label]: !prev[label],
      }));
    } else {
      setOpenSubmenus({});
    }
  };

  const getPageTitle = useCallback(() => {
    if (!navItems || navItems.length === 0) return "Loading...";

    if (location.pathname.startsWith("/rfi_document/"))
      return "RFI - Documents";

    if (location.pathname.startsWith("/viewRFI/")) return "RFI Stages";

    // Check main items
    const foundMainItem = navItems.find(
      (item) => item.path === location.pathname
    );
    if (foundMainItem) return foundMainItem.label;

    // Check submenu items
    for (const item of navItems) {
      if (item.submenu && item.submenuItems) {
        const foundSubItem = item.submenuItems.find(
          (subItem) => subItem.path === location.pathname
        );
        if (foundSubItem) return foundSubItem.label;
      }
    }
    if (location.pathname.startsWith("/createRFI")) return "Create RFI";
    return "Dashboard";
  }, [location.pathname, navItems]);

  useEffect(() => {
    if (navItems && navItems.length > 0) {
      setSelectedPage(getPageTitle());

      // Auto-expand submenus if on a submenu item
      navItems.forEach((item) => {
        if (item.submenu) {
          const isSubItemActive = item.submenuItems.some(
            (subItem) => subItem.path === location.pathname
          );
          if (isSubItemActive) {
            setOpenSubmenus((prev) => ({ ...prev, [item.label]: true }));
          }
        }
      });
    }
  }, [getPageTitle, location.pathname, navItems]);

  // Check if we can go back in history
  useEffect(() => {
    // We consider login and dashboard as root routes where back button should be disabled
    const isRootRoute =
      location.pathname === "/dashboard" || location.pathname === "/login";

    if (location.pathname !== "/dashboard") {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }

    // window.history.length > 1 means we have history entries to go back to
    const hasHistory = window.history.length > 1;

    // Only enable back button if we're not on a root route and we have history
    setCanGoBack(hasHistory && !isRootRoute);
  }, [location.pathname]);

  // Show loader if navItems is empty array (which means still loading)
  if (navItems.length === 0) {
    return (
      <ThemeProvider theme={theme}>
        <ScopedCssBaseline />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            width: "100vw",
            backgroundColor: theme.palette.background.default,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ScopedCssBaseline />
      <Box sx={styles.root}>
        <CssBaseline />

        <AppBar position="fixed" sx={styles.appBar(collapsed)}>
          <Toolbar sx={styles.toolbar}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyItems: "center",
              }}
            >
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                sx={{ display: { xs: "flex", md: "none" }, fontSize: 28 }}
              >
                <MenuIcon />
              </IconButton>

              {canGoBack && (
                <IconButton
                  color="inherit"
                  aria-label="go back"
                  onClick={handleGoBack}
                  sx={{
                    mr: 1,
                    fontSize: 28,
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              <DashboardIcon
                sx={{
                  display: { xs: "none", md: "block" },
                  fontSize: 28,
                  mr: 1,
                }}
              />
              <Typography variant="h6">{selectedPage}</Typography>
            </Box>
            <NotificationDropdown />
          </Toolbar>
        </AppBar>

        <Box component="nav" sx={styles.drawerNav(collapsed)}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": styles.drawerPaperMobile,
            }}
          >
            <Sidebar
              collapsed={collapsed}
              handleCollapseToggle={handleCollapseToggle}
              navItems={navItems}
              selectedPage={selectedPage}
              openSubmenus={openSubmenus}
              toggleSubmenu={toggleSubmenu}
              navigate={navigate}
              handleLogout={handleLogout}
              setCollapsed={setCollapsed}
            />
          </Drawer>

          <Drawer
            variant="permanent"
            open
            sx={{
              display: { xs: "none", md: "block" },
              "& .MuiDrawer-paper": styles.drawerPaper(collapsed),
            }}
          >
            <Sidebar
              collapsed={collapsed}
              handleCollapseToggle={handleCollapseToggle}
              navItems={navItems}
              selectedPage={selectedPage}
              openSubmenus={openSubmenus}
              toggleSubmenu={toggleSubmenu}
              navigate={navigate}
              handleLogout={handleLogout}
              setCollapsed={setCollapsed}
            />
          </Drawer>
        </Box>

        <Box component="main" sx={styles.main}>
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
